/* eslint-disable object-curly-newline */

/**
Author - Vikrant Singh
Git - git@github.com:vikrantSingh97
**/
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {ActionButton, Spinner} from 'screens/commons';
import {ProgressBar} from 'components/ProgressBar';

const propTypes = {
  kycData: PropTypes.object.isRequired,
  handleSubFlow: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  timer: PropTypes.number.isRequired,
  isSubflowPresent: PropTypes.bool.isRequired,
  handleRedirection: PropTypes.func.isRequired,
  skippingSuccessScreen: PropTypes.bool.isRequired,
  isRedirectLoading: PropTypes.bool.isRequired,
};

const PartnerKycSuccessScreen = ({
  handleSubFlow,
  kycData,
  isRedirectLoading,
  timer,
  isSubflowPresent,
  handleRedirection,
  skippingSuccessScreen,
}) => {
  const successScreenImage = 'https://i.wlycdn.com/wealthy-kyc/kyc-success-tick.svg';
  const defaultData = {
    title: 'KYC Application Submitted Successfully',
    content: '',
    ctaText: 'Continue to App',
    link: '',
  };

  let subHeaderText =
    'You can click on the button below to go back to the app or you will be redirected automatically in';

  const showSkipButton = false; //TODO: Discuss and update this

  const isSuccessContentPresent = !!kycData.successContent?.title;
  const title = isSuccessContentPresent ? kycData.successContent?.title : defaultData.title;
  const message = isSuccessContentPresent ? kycData.successContent?.msg : defaultData.msg;
  let ctaText = isSuccessContentPresent ? kycData.successContent?.cta : defaultData.ctaText;
  const link = isSuccessContentPresent ? kycData.successContent?.link : defaultData.link;
  const progressPercentage = 100 - ((8 - timer) / 8) * 100;

  // Explicit condition checks
  const isFlowPartnerGST = kycData?.flow === 'PARTNER_GST';

  if (isSubflowPresent) {
    ctaText = kycData.subFlow?.ctaName ? kycData.subFlow.ctaName : defaultData.ctaText;
  } else if (isFlowPartnerGST) {
    subHeaderText =
      'To continue with the empanelment process, please click the button below or you will be redirected automatically in ';
  }

  return isRedirectLoading || skippingSuccessScreen ? (
    <LoaderContainer>
      <ProgressBar
        heading="We are fetching next steps"
        subheading=""
        waitTimeInSec={100}
        loadingTexts={[]}
        showShadow={false}
      />
    </LoaderContainer>
  ) : (
    <SuccessScreenWrapper>
      <SuccessContent showSkipButton={showSkipButton}>
        {showSkipButton ? (
          <Header>
            <Caption onClick={handleRedirection}>Skip</Caption>
          </Header>
        ) : (
          <></>
        )}
        <ImageContainer>
          <img alt="connect-with-wealth-partner" src={successScreenImage} />
        </ImageContainer>
        <Title>{title}</Title>
        {message ? <SubHeader dangerouslySetInnerHTML={{__html: message}} /> : null}
        <Link dangerouslySetInnerHTML={{__html: link}} />
        {!isSubflowPresent ? (
          <Link>
            {subHeaderText} {timer} seconds
            <BarTrack>
              <BarFill style={{width: `${progressPercentage}%`}} />
            </BarTrack>
          </Link>
        ) : null}
        <Footer>
          <ActionButton
            buttonText={ctaText}
            callback={handleSubFlow}
            isDisabled={isRedirectLoading}
          />
        </Footer>
      </SuccessContent>
    </SuccessScreenWrapper>
  );
};

const BarTrack = styled.div`
  width: 100%;
  height: 8px;
  background: ${({theme}) => theme.colors.background.secondary || '#f3f4f6'};
  border-radius: 9999px;
  overflow: hidden;
  margin-top: 1.25rem;
`;

const BarFill = styled.div`
  height: 100%;
  background: ${({theme}) => theme.colors.brand.primary};
  border-radius: 9999px;
  transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: 0.9;
`;

const SuccessScreenWrapper = styled.div`
  height: 100%;
  width: 100%;
  scroll-behavior: scroll;
  position: relative;
  overflow-y: scroll;
`;

const LoaderContainer = styled.div`
  height: 80vh;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;

  @media (max-width: 768px) {
    width: 85%;
    margin: 0 auto;
  }
`;

const SuccessContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  height: 100%;
`;

const ImageContainer = styled.div`
  margin-top: 2rem;
`;

const Title = styled.h1.attrs({
  className: 'body-medium',
})`
  white-space: pre-wrap;
  margin: 0;
  padding: 0;
  margin-top: 2rem;
  text-align: center;
`;

// const Title = styled.h1.attrs({
//   className: 'primary-title'
// })`
//   white-space: pre-wrap;
//   margin: 0;
//   padding: 0;
//   margin-top: 3.6rem;
//   line-height: 1.4;
//   letter-spacing: 0.1rem;
//   text-align: center;
// `;

const Caption = styled.span.attrs({
  className: 'caption',
})`
  margin: 0;
  padding: 0;
  color: #6725f4;

  align-self: end;
  cursor: pointer;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: end;
  width: 100%;
  flex-shrink: 1;
`;

const Footer = styled.div`
  width: 100%;
  max-width: 600px;
  margin-top: 2rem;
`;

const SubHeader = styled.p.attrs({
  className: 'body-small',
})`
  padding: 1rem;
  text-align: center;
  color: #7e7e7e;
  max-width: 90%;
  line-height: normal;
  :not(:first-child) {
    margin-top: 2rem;
  }
  border-radius: 8px;
  background-color: ${(props) => props.theme.colors.background.secondary};
  color: ${(props) => props.theme.colors.brand.primary};
`;

const Link = styled.p.attrs({
  className: 'body-small',
})`
  text-align: center;
  color: #7e7e7e;
  max-width: 400px;
`;

PartnerKycSuccessScreen.propTypes = propTypes;

export default PartnerKycSuccessScreen;
